import { Injectable } from '@angular/core';
import { deepCopy } from '../../../utils/object/object';
import { Store } from '@ngxs/store';
import { AddCustomerInfo } from '../../../actions/customer-action/customer.action';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from '@shared/features/authentication/services/auth.service';
import { UntypedFormGroup } from '@angular/forms';
import { AccountApiService } from '@shared/features/account/services/account-api.service';
import { UserAccount, UserProfile } from '@shared/features/account/interfaces/customer.interface';
import {
  AccountApiError,
  ClaimBooking,
  SignUpPayload
} from '@shared/features/account/interfaces/account-api.interface';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  profileUpdateSubject = new Subject<{ isComplete: boolean; completePercentage: number }>();
  constructor(
    private readonly store: Store,
    private readonly authService: AuthService,
    private readonly accountApiService: AccountApiService
  ) {}

  getCustomerToken() {
    return this.authService.authToken;
  }

  signUp(data: SignUpPayload) {
    return this.accountApiService.signUp(data);
  }

  activateCustomer(token: string) {
    return this.accountApiService.activateCustomer(token);
  }

  getCustomer(fromState: boolean = false): Observable<UserAccount> {
    const authToken = this.authService.authToken;

    if (fromState) {
      const stateCustomer = this.store.selectSnapshot((state) => state.customer?.customer);
      if (stateCustomer) {
        return of(deepCopy(stateCustomer));
      }
      return this.getCustomer(false);
    }

    return this.accountApiService.getCustomer(authToken).pipe(
      map((response) => {
        if (response.success) {
          this.dispatchAddCustomerInfo(response.payload);
          return response.payload as UserAccount;
        }
        throw new Error(response.message);
      }),
      catchError((error: AccountApiError) => throwError(() => error))
    );
  }

  updateProfile(data: Partial<UserProfile>): Observable<UserAccount> {
    return this.accountApiService.updateCustomer(data).pipe(
      switchMap((response) => {
        if (response.success) {
          const customerSnapshot: UserAccount = this.store.selectSnapshot((state) => state.customer?.customer);

          const customer = deepCopy(customerSnapshot);
          customer.profile = response.payload;
          return this.dispatchAddCustomerInfo(customer).pipe(map(() => deepCopy(response.payload)));
        } else {
          return throwError(() => new Error(response.message));
        }
      }),
      catchError((error: AccountApiError) => {
        return throwError(() => error);
      })
    );
  }

  setPassword(data) {
    return this.accountApiService.setPassword(data);
  }

  resetPassword(password: string, token: string) {
    return this.accountApiService.resetPassword(password, token);
  }

  resetPasswordRequest(email: string, recaptcha: string) {
    return this.accountApiService.resetPasswordRequest(email, recaptcha);
  }

  claimBooking(data: ClaimBooking) {
    return this.accountApiService.claimBooking(data);
  }

  checkResetPasswordToken(token: string) {
    return this.accountApiService.verifyResetPasswordToken(token);
  }

  getReservations(queryParams?: string) {
    return this.accountApiService.getReservations(queryParams).pipe(
      map((response) => response.payload),
      catchError((err: AccountApiError) => {
        return throwError(() => err);
      })
    );
  }

  getReservationDetails(id: string) {
    return this.accountApiService.getReservationDetails(id).pipe(
      map((response) => response.payload),
      catchError((err: AccountApiError) => {
        return throwError(() => err);
      })
    );
  }

  /**
   * Checks if the user profile is completed (e.g. all fields have values)
   * @param profile CustomerProfile
   * @returns boolean
   */
  isProfileComplete(profile: UserProfile) {
    if (!profile) {
      return false;
    }

    const emptyFields = Object.keys(profile).some((key) => {
      if (key === 'gender') {
        return false;
      }
      return !profile[key];
    });

    const emptyAddressFields = Object.keys(profile.address).some(
      (key) => !profile.address[key] || !/\S/.test(profile.address[key])
    );

    return !emptyFields && !emptyAddressFields;
  }

  /**
   * Calculates how much the profile is filled in by the user and returns a percentage
   *
   * @param profile CustomerProfile
   * @returns number (0-100)
   */
  profileCompletionPercentage(profile: UserProfile): number {
    if (!profile) {
      return 0;
    }

    // TODO: tests
    const { address, gender, ...user } = profile;
    const totalFields = [...Object.keys(user), ...Object.keys(address)]?.length;
    const completedFields = [...Object.values(user), ...Object.values(address)]?.filter(Boolean)?.length ?? 0;

    return (completedFields / totalFields) * 100;
  }

  dispatchAddCustomerInfo(customerInfo: UserAccount) {
    return this.store.dispatch(new AddCustomerInfo(customerInfo));
  }

  matchPasswords(controlName: string, matchingControlName: string) {
    return (formGroup: UntypedFormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }
}
